<script setup lang='ts'>
  import { useImage } from '@/composables/useImage'
  import type { SlideSideBySide } from '@/types/presentation/SlideSideBySide'

  import ImageCaption from '@/components/common/ImageCaption.vue'
  import ImageWithCaption from '@/components/common/ImageWithCaption.vue'
  import SlideHeading from '@/components/presentation/SlideHeading.vue'
  import SlideText from '@/components/presentation/SlideText.vue'

  const props = defineProps<{
    slide: SlideSideBySide
  }>()
  const { altText, copyright, caption, getImageByField } = useImage({ imageId: props.slide.image.destinationContentId })

  const imageSource = getImageByField(props.slide.image.destinationContentId)
</script>

<template>
  <div :class="['slide-side-by-side', slide.mainElementPosition]">
    <div class="image-column">
      <ImageWithCaption
        v-if="imageSource"
        :src="imageSource"
        :alt-text="slide.image.alternativeText ?? altText"
        object-fit="cover"
        class="image"
      >
        <template #imageCaption>
          <ImageCaption
            is-following-color-pair
            :copyright="copyright"
            class="image-caption"
          />
        </template>
      </ImageWithCaption>
    </div>
    <div class="text-column">
      <div :class="['text-wrapper', slide.textPosition ]">
        <SlideHeading
          v-if="slide?.heading"
          :size="slide?.headingFontSize"
          :heading="slide?.heading"
        />
        <SlideText
          v-if="slide?.text"
          :size="slide?.textFontSize"
          :text="slide?.text"
        />
      </div>
    </div>
  </div>
</template>

<style lang='scss' scoped>
  .image-caption {
    position: absolute;
    bottom: 0;
    right: 0;
  }

  .slide-side-by-side {
    display: flex;
    height: 100%;
  }

  .image-column,
  .text-column {
    flex: 1;
  }

  .text-column {
    overflow-y: auto;
  }

  .text-wrapper {
    display: flex;
    flex-direction: column;
    min-height: 100%;
    gap: 40px;
    padding: 80px;
  }

  .center-left {
    justify-content: center;
  }

  .image-column {
    :deep(.image-with-caption-container) {
      height: 100%;
    }
  }

  .right,
  .right-narrow,
  .right-wide {
    flex-direction: row-reverse;
  }

  .left-wide,
  .right-wide {
    .image-column {
      flex: 2;
    }
  }

  .left-narrow,
  .right-narrow {
    .text-column {
      flex: 2;
    }
  }

  .left {
    flex: 1;
  }
</style>
